import { SVGName, ValidationError } from 'roadie-ui'
import { Dispatch } from 'react'
import { createVehicle } from 'actions/vehicles'
import { addDriverCert } from 'actions/profile'
import { DrivingProfile, ProfileVehicle } from 'types/profile'
import { optimizely } from 'Optimizely'
import type { TranslationKey } from 'localization'

export type VehicleType = 'Car' | 'SUV' | 'Van' | 'Pickup' | 'Truck'

type LocalizedValidationError = ValidationError & { messageKey?: TranslationKey }

const localizedValidationError = (message: string, messageKey: TranslationKey): LocalizedValidationError => {
  const error = new ValidationError(message) as LocalizedValidationError
  error.messageKey = messageKey
  return error
}

type VehicleIcon = {
  type: VehicleType
  icon: SVGName
  labelKey: TranslationKey
}

type VehicleIcons = VehicleIcon[]

export const vehicleTypes: VehicleIcons = [
  { type: 'Car', icon: 'VehicleCar', labelKey: 'dweb_vehicle_type_car' },
  { type: 'SUV', icon: 'VehicleSuv', labelKey: 'dweb_vehicle_type_suv' },
  { type: 'Van', icon: 'VehicleVan', labelKey: 'dweb_vehicle_type_van' },
  { type: 'Pickup', icon: 'VehiclePickup', labelKey: 'dweb_vehicle_type_pickup' },
  { type: 'Truck', icon: 'VehicleTruck', labelKey: 'dweb_vehicle_type_truck' },
]

type VehicleColor = {
  labelKey: TranslationKey
  value: string
  hex: string
}

type VehicleColors = VehicleColor[]

export const vehicleColors: VehicleColors = [
  { labelKey: 'dweb_vehicle_color_grey', value: 'grey', hex: '#7A7A7A' },
  { labelKey: 'dweb_vehicle_color_black', value: 'black', hex: 'black' },
  { labelKey: 'dweb_vehicle_color_white', value: 'white', hex: 'white' },
  { labelKey: 'dweb_vehicle_color_blue', value: 'blue', hex: '#2054cf' },
  { labelKey: 'dweb_vehicle_color_red', value: 'red', hex: '#e34025' },
  { labelKey: 'dweb_vehicle_color_silver', value: 'silver', hex: '#D2D2D2' },
  { labelKey: 'dweb_vehicle_color_green', value: 'green', hex: '#508630' },
  { labelKey: 'dweb_vehicle_color_orange', value: 'orange', hex: '#ff9900' },
  { labelKey: 'dweb_vehicle_color_brown', value: 'brown', hex: '#814d00' },
  { labelKey: 'dweb_vehicle_color_purple', value: 'purple', hex: '#680cb8' },
  { labelKey: 'dweb_vehicle_color_yellow', value: 'yellow', hex: '#fedc89' },
  { labelKey: 'dweb_vehicle_color_beige', value: 'beige', hex: '#f5f5dc' },
]

export const vehicleCategories = [
  'two_door', // 0
  'sedan', // 1
  'hatchback_suv', // 2
  'van', // 3
  'pickup_truck', // 4
  'box_truck', // 5
  'cargo_van', // 6
  'large_box_truck', // 7
  'oversized_box_truck', // 8
] as const

export type VehicleCategory = (typeof vehicleCategories)[number]

export function getCategoryByEnumVal(enumVal: number): VehicleCategory {
  return vehicleCategories[enumVal]
}

export type VehicleSubType = {
  name: string
  desc: string
  labelKey: TranslationKey
  descriptionKey: TranslationKey
  enumVal: number
  make?: string
  model: string
}

type VehicleSubTypes = Record<VehicleType, VehicleSubType[]>

export const vehicleSubTypes: VehicleSubTypes = {
  Car: [
    {
      name: 'Compact Car',
      desc: 'Honda Civic, Ford Focus, VW Jetta, etc.',
      labelKey: 'dweb_vehicle_subtype_compact_car',
      descriptionKey: 'dweb_vehicle_subtype_compact_car_description',
      enumVal: 0,
      make: 'Compact',
      model: 'Car',
    },
    {
      name: 'Full-Sized Car',
      desc: 'Honda Accord, Chevy Malibu, Chrysler 300, etc.',
      labelKey: 'dweb_vehicle_subtype_full_sized_car',
      descriptionKey: 'dweb_vehicle_subtype_full_sized_car_description',
      enumVal: 1,
      make: 'Full-Sized',
      model: 'Car',
    },
    {
      name: 'Hatchback',
      desc: 'VW Golf, Toyota Prius, Honda Civic Hatchback, etc.',
      labelKey: 'dweb_vehicle_subtype_hatchback',
      descriptionKey: 'dweb_vehicle_subtype_hatchback_description',
      enumVal: 2,
      model: 'Hatchback',
    },
  ],
  SUV: [
    {
      name: 'Compact SUV',
      desc: 'Honda CRV, Mazda CX-5, Toyota RAV4, etc.',
      labelKey: 'dweb_vehicle_subtype_compact_suv',
      descriptionKey: 'dweb_vehicle_subtype_compact_suv_description',
      enumVal: 2,
      make: 'Compact',
      model: 'SUV',
    },
    {
      name: 'Large SUV',
      desc: 'Ford Expedition, GMC Yukon, Toyota Sequoia, etc.',
      labelKey: 'dweb_vehicle_subtype_large_suv',
      descriptionKey: 'dweb_vehicle_subtype_large_suv_description',
      enumVal: 3,
      make: 'Large',
      model: 'SUV',
    },
  ],
  Van: [
    {
      name: 'Minivan',
      desc: 'Toyota Sienna, Honda Odyssey, etc.',
      labelKey: 'dweb_vehicle_subtype_minivan',
      descriptionKey: 'dweb_vehicle_subtype_minivan_description',
      enumVal: 3,
      model: 'Minivan',
    },
    {
      name: 'Cargo Van',
      desc: 'Ford Transit, Chevy Express, Nissan NV, etc.',
      labelKey: 'dweb_vehicle_subtype_cargo_van',
      descriptionKey: 'dweb_vehicle_subtype_cargo_van_description',
      enumVal: 5,
      make: 'Cargo',
      model: 'Van',
    },
  ],
  Pickup: [
    {
      name: 'Short Bed Pickup',
      desc: 'Less than 6 ft of bed length',
      labelKey: 'dweb_vehicle_subtype_short_bed_pickup',
      descriptionKey: 'dweb_vehicle_subtype_short_bed_pickup_description',
      enumVal: 2,
      make: 'Short Bed',
      model: 'Pickup',
    },
    {
      name: 'Standard Bed Pickup',
      desc: '6-7 ft of bed length',
      labelKey: 'dweb_vehicle_subtype_standard_bed_pickup',
      descriptionKey: 'dweb_vehicle_subtype_standard_bed_pickup_description',
      enumVal: 3,
      make: 'Standard Bed',
      model: 'Pickup',
    },
    {
      name: 'Long Bed Pickup',
      desc: 'More than 7 ft of bed length',
      labelKey: 'dweb_vehicle_subtype_long_bed_pickup',
      descriptionKey: 'dweb_vehicle_subtype_long_bed_pickup_description',
      enumVal: 4,
      make: 'Long Bed',
      model: 'Pickup',
    },
  ],
  Truck: [
    {
      name: 'Small Box Truck',
      desc: '12-15 ft',
      labelKey: 'dweb_vehicle_subtype_small_box_truck',
      descriptionKey: 'dweb_vehicle_subtype_small_box_truck_description',
      enumVal: 6,
      make: 'Small',
      model: 'Box Truck',
    },
    {
      name: 'Medium Box Truck',
      desc: '16-25 ft',
      labelKey: 'dweb_vehicle_subtype_medium_box_truck',
      descriptionKey: 'dweb_vehicle_subtype_medium_box_truck_description',
      enumVal: 7,
      make: 'Medium',
      model: 'Box Truck',
    },
    {
      name: 'Large Box Truck',
      desc: '26+ ft',
      labelKey: 'dweb_vehicle_subtype_large_box_truck',
      descriptionKey: 'dweb_vehicle_subtype_large_box_truck_description',
      enumVal: 8,
      make: 'Large',
      model: 'Box Truck',
    },
  ],
}

export type VehicleFormStates =
  | 'searchingVehicle'
  | 'vehicleFound'
  | 'vehicleNotFound'
  | 'retryLicensePlate'
  | 'conflictingVINFound'
  | 'unsupportedType'
  | 'serverError'
  | null

export type CreateVehicleRequestPayload = {
  year?: string
  make?: string
  model?: string
  category?: string
  color: string
  license_plate: string
  state: string
}

export type CreateVehicleResponsePayload = {
  id: number
  year?: string
  make?: string
  model?: string
  category?: string
  color: string
}

export function getVehicleDisplayName(vehicle: Pick<ProfileVehicle, 'details'> | null | undefined) {
  return vehicle?.details?.display_name?.trim() || 'Car'
}

// Keep this function in case we go back to using colors for the Vehicle Color Select
export function getVehicleHex(colorName: string) {
  for (const vehicleColor of vehicleColors) {
    if (vehicleColor.value === colorName) {
      return vehicleColor.hex
    }
  }

  return ''
}

export function validateNewVehicleData(dispatch: Dispatch<any>, data: any, inRegistration: boolean = false) {
  const { color, category, subcategory, license_plate, license_state } = data as {
    color: string
    category: VehicleType
    subcategory: string
    license_plate: string
    license_state: string
  }

  if (!color) {
    dispatch({ type: 'PUSH_FLASH', flash: { messageKey: 'dweb_flash_vehicle_color_required', type: 'error' } })
    throw new Error()
  }

  if (inRegistration && !category) {
    dispatch({ type: 'PUSH_FLASH', flash: { messageKey: 'dweb_flash_vehicle_category_required', type: 'error' } })
    throw new Error()
  }

  if (inRegistration && !subcategory) {
    dispatch({ type: 'PUSH_FLASH', flash: { messageKey: 'dweb_flash_vehicle_subcategory_required', type: 'error' } })
    throw new Error()
  }

  const featureFlag = optimizely.decide('drive_license_plate_registration_lookup')
  // TODO: Removing this feature flag eventually is the goal
  if (featureFlag.enabled) {
    if (!license_plate) {
      dispatch({
        type: 'PUSH_FLASH',
        flash: { messageKey: 'dweb_flash_vehicle_license_plate_required', type: 'error' },
      })
      throw new Error()
    }

    if (!license_state) {
      dispatch({
        type: 'PUSH_FLASH',
        flash: { messageKey: 'dweb_flash_vehicle_license_plate_state_required', type: 'error' },
      })
      throw new Error()
    }
  }

  if (inRegistration) {
    const subType = vehicleSubTypes[category].find((sub) => sub.name === subcategory)
    if (!subType) {
      dispatch({ type: 'PUSH_FLASH', flash: { messageKey: 'dweb_flash_vehicle_invalid_subcategory', type: 'error' } })
      throw new Error()
    }
  }
}

export async function submitNewVehicleData(
  dispatch: Dispatch<any>,
  data: {
    color: string
    license_plate: string
    license_state: string
    category?: VehicleType
    subcategory?: string
  },
  firstVehicle: boolean = false
) {
  const { color, category, subcategory, license_plate, license_state } = data

  let make: string | undefined
  let model: string | undefined
  let enumVal: number | undefined
  let vehicleCategory: string | undefined

  if (category && subcategory) {
    const subType = vehicleSubTypes[category].find((sub) => sub.name === subcategory) as VehicleSubType
    if (subType) {
      make = subType.make
      model = subType.model
      enumVal = subType.enumVal
      vehicleCategory = getCategoryByEnumVal(enumVal)
    }
  } else {
    // Default to sedan if category or subcategory is not provided
    vehicleCategory = 'sedan'
  }

  // TODO: define shape of create vehicle request payload
  const newVehicle = await createVehicle({
    color,
    category: vehicleCategory,
    make,
    model,
    enum: enumVal,
    license_plate,
    state: license_state,
  })

  if (firstVehicle) {
    Promise.all([category != 'Pickup' && addDriverCert('enclosed_vehicle')(dispatch)])
  }

  return newVehicle.data
}

export const validateExpirationDate = (value: string): LocalizedValidationError | null => {
  if (!value || value.length !== 8) {
    return localizedValidationError('Date must be in MM/DD/YYYY format', 'dweb_validation_date_must_be_mm_dd_yyyy')
  }

  const month = parseInt(value.substring(0, 2), 10)
  const day = parseInt(value.substring(2, 4), 10)
  const year = parseInt(value.substring(4, 8), 10)

  // Validate month
  if (month < 1 || month > 12) {
    return localizedValidationError(
      'Month must be between 01 and 12',
      'dweb_validation_month_must_be_between_01_and_12'
    )
  }

  // Validate day based on month
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

  // Check for leap year
  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
  if (isLeapYear && month === 2) {
    daysInMonth[1] = 29
  }

  if (day < 1 || day > daysInMonth[month - 1]) {
    return localizedValidationError('Invalid day for the given month', 'dweb_validation_invalid_day_for_given_month')
  }

  // Validate date is in the future
  const inputDate = new Date(year, month - 1, day)
  const today = new Date()
  today.setHours(0, 0, 0, 0) // Reset time to start of day for fair comparison

  if (inputDate <= today) {
    return localizedValidationError(
      'Expiration date must be in the future',
      'dweb_validation_expiration_date_must_be_future'
    )
  }

  return null
}

export const validateDriversLicenseData = (dispatch: Dispatch<any>, data: any) => {
  if (!data.number) {
    dispatch({ type: 'PUSH_FLASH', flash: { messageKey: 'dweb_flash_license_number_required', type: 'error' } })
    throw new Error()
  }
  if (!data.state) {
    dispatch({ type: 'PUSH_FLASH', flash: { messageKey: 'dweb_flash_license_state_required', type: 'error' } })
    throw new Error()
  }
  if (!data.expiration_date) {
    dispatch({
      type: 'PUSH_FLASH',
      flash: { messageKey: 'dweb_flash_license_expiration_date_required', type: 'error' },
    })
    throw new Error()
  }

  // Validate expiration date format and validity
  const dateValidationError = validateExpirationDate(data.expiration_date)
  if (dateValidationError) {
    dispatch({
      type: 'PUSH_FLASH',
      flash: { message: dateValidationError.message, messageKey: dateValidationError.messageKey, type: 'error' },
    })
    throw new Error()
  }
}

/** Prevents special characters from being entered in input fields like license plate */
export const preventSpecialCharacters = (e: React.KeyboardEvent<HTMLInputElement>) => {
  const regex = /^[a-zA-Z0-9]+$/
  if (!regex.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') {
    e.preventDefault() // Prevent any non-alphanumeric key from being entered
  }
}

export const getActiveVehicleId = (vehicles: ProfileVehicle[]) => {
  return vehicles.find((vehicle) => vehicle.selected_as_active)?.details?.id
}

/** Checks if a vehicle has a license plate lookup failure. */
export const hasLicensePlateLookupFailure = (vehicle: ProfileVehicle) => {
  return (
    vehicle?.plate_lookup &&
    (vehicle?.plate_lookup?.failure_reason == 'no_vehicle_found' ||
      vehicle?.plate_lookup?.failure_reason == 'unsupported_category' ||
      vehicle?.plate_lookup?.result == 'failure')
  )
}

export function getLicenseExpirationDate(license: NonNullable<DrivingProfile['drivers_license']>) {
  const [year, month, day] = (license.expiration_date || '').split('-')
  if (month && day && year) {
    return `${month}/${day}/${year}`
  }

  return ''
}
